import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Key, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setError('Lien invalide : aucun token trouvé');
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      setVerifying(true);
      const response = await api.post('/auth/reset-password/verify', { token });
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (err) {
      console.error('Token verification error:', err);
      setTokenValid(false);
      setError(err.response?.data?.detail || 'Ce lien de réinitialisation est invalide ou a expiré');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/reset-password/confirm', {
        token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès !');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Password reset error:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Vérification du lien...</p>
              </CardContent>
            </Card>
          </div>
        </section>
      </StoreLayout>
    );
  }

  if (!tokenValid) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardHeader className="text-center">
                  <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <CardTitle>Lien invalide</CardTitle>
                  <CardDescription>{error}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Ce lien de réinitialisation est invalide, a expiré ou a déjà été utilisé.
                      Les liens de réinitialisation expirent après 24 heures.
                    </AlertDescription>
                  </Alert>
                  <div className="flex flex-col gap-2">
                    <Button onClick={() => navigate('/login')}>
                      Retour à la connexion
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/login?forgot=true')}>
                      Demander un nouveau lien
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      </StoreLayout>
    );
  }

  if (success) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardHeader className="text-center">
                  <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <CardTitle>Mot de passe réinitialisé</CardTitle>
                  <CardDescription>
                    Votre mot de passe a été modifié avec succès
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                      Redirection automatique...
                    </AlertDescription>
                  </Alert>
                  <Button className="w-full" onClick={() => navigate('/login')}>
                    Se connecter maintenant
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Key className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Réinitialiser votre mot de passe</CardTitle>
                <CardDescription>
                  Choisissez un nouveau mot de passe pour {email}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="new_password">Nouveau mot de passe</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Au moins 6 caractères"
                      required
                      minLength={6}
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Retapez votre mot de passe"
                      required
                      minLength={6}
                    />
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Réinitialisation...
                      </>
                    ) : (
                      <>
                        <Key className="mr-2 h-4 w-4" />
                        Réinitialiser le mot de passe
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => navigate('/login')}
                    >
                      Retour à la connexion
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </StoreLayout>
  );
}
